/**
 * Procedural synth sounds — 14 fire-and-forget synthesis functions.
 *
 * Each function creates oscillators/noise → connects to a provided GainNode →
 * schedules stop. No persistent audio nodes. All durations are approximate.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Create a white noise buffer (1 second, mono). */
function createNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const length = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/** Create a noise source node. */
function noiseSource(ctx: AudioContext): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = createNoiseBuffer(ctx);
  return src;
}

// ─── Sound Functions ────────────────────────────────────────────────────────

/**
 * Explosion — White noise burst → lowpass sweep 2kHz→80Hz + sine sub-bass 60Hz.
 * Duration: ~0.5s
 */
export function explosion(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  // Noise burst through sweeping lowpass
  const noise = noiseSource(ctx);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.setValueAtTime(2000, t);
  lpf.frequency.exponentialRampToValueAtTime(80, t + 0.5);
  lpf.Q.value = 1.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.8, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, t + 0.5);
  noise.connect(lpf).connect(noiseGain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.5);

  // Sub-bass sine
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.value = 60;
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.6, t);
  subGain.gain.exponentialRampToValueAtTime(0.01, t + 0.5);
  sub.connect(subGain).connect(dest);
  sub.start(t);
  sub.stop(t + 0.5);
}

/**
 * Fire/Launch — Sawtooth sweep 400→1200Hz + noise crackle.
 * Duration: ~0.2s
 */
export function fire(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  // Sawtooth sweep
  const saw = ctx.createOscillator();
  saw.type = 'sawtooth';
  saw.frequency.setValueAtTime(400, t);
  saw.frequency.exponentialRampToValueAtTime(1200, t + 0.15);
  const sawGain = ctx.createGain();
  sawGain.gain.setValueAtTime(0.3, t);
  sawGain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
  saw.connect(sawGain).connect(dest);
  saw.start(t);
  saw.stop(t + 0.2);

  // Noise crackle
  const noise = noiseSource(ctx);
  const hpf = ctx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = 3000;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.2, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
  noise.connect(hpf).connect(noiseGain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.2);
}

/**
 * Hit — Square 200→100Hz pitch bend, lowpassed.
 * Duration: ~0.15s
 */
export function hit(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(200, t);
  osc.frequency.exponentialRampToValueAtTime(100, t + 0.15);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 800;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
  osc.connect(lpf).connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.15);
}

/**
 * Death — Sawtooth 800→100Hz sweep + distorted square + noise tail.
 * Duration: ~0.8s
 */
export function death(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  // Sawtooth sweep
  const saw = ctx.createOscillator();
  saw.type = 'sawtooth';
  saw.frequency.setValueAtTime(800, t);
  saw.frequency.exponentialRampToValueAtTime(100, t + 0.6);
  const sawGain = ctx.createGain();
  sawGain.gain.setValueAtTime(0.35, t);
  sawGain.gain.exponentialRampToValueAtTime(0.01, t + 0.7);
  saw.connect(sawGain).connect(dest);
  saw.start(t);
  saw.stop(t + 0.7);

  // Distorted square
  const sq = ctx.createOscillator();
  sq.type = 'square';
  sq.frequency.setValueAtTime(150, t);
  sq.frequency.exponentialRampToValueAtTime(50, t + 0.5);
  const clip = ctx.createWaveShaper();
  const curve = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const x = (i / 128) - 1;
    curve[i] = Math.tanh(x * 3);
  }
  clip.curve = curve;
  const sqGain = ctx.createGain();
  sqGain.gain.setValueAtTime(0.2, t);
  sqGain.gain.exponentialRampToValueAtTime(0.01, t + 0.6);
  sq.connect(clip).connect(sqGain).connect(dest);
  sq.start(t);
  sq.stop(t + 0.6);

  // Noise tail
  const noise = noiseSource(ctx);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.setValueAtTime(1200, t);
  lpf.frequency.exponentialRampToValueAtTime(80, t + 0.8);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.15, t + 0.2);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, t + 0.8);
  noise.connect(lpf).connect(noiseGain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.8);
}

/**
 * Bounce — Triangle 600→900Hz pitch up.
 * Duration: ~0.08s
 */
export function bounce(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(600, t);
  osc.frequency.exponentialRampToValueAtTime(900, t + 0.08);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.08);
  osc.connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.08);
}

/**
 * Menu Select — Two-note sine chime 880Hz→1320Hz.
 * Duration: ~0.13s
 */
export function menuSelect(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  // First note
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = 880;
  const g1 = ctx.createGain();
  g1.gain.setValueAtTime(0.25, t);
  g1.gain.exponentialRampToValueAtTime(0.01, t + 0.07);
  osc1.connect(g1).connect(dest);
  osc1.start(t);
  osc1.stop(t + 0.07);

  // Second note (staggered)
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = 1320;
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.25, t + 0.05);
  g2.gain.exponentialRampToValueAtTime(0.01, t + 0.13);
  osc2.connect(g2).connect(dest);
  osc2.start(t + 0.05);
  osc2.stop(t + 0.13);
}

/**
 * Menu Nav — Single sine blip 660Hz.
 * Duration: ~0.05s
 */
export function menuNav(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 660;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.2, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
  osc.connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.05);
}

/**
 * Turn Transition — Rising sine sweep 300→600Hz.
 * Duration: ~0.3s
 */
export function turnTransition(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(300, t);
  osc.frequency.exponentialRampToValueAtTime(600, t + 0.25);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.25, t);
  gain.gain.linearRampToValueAtTime(0.3, t + 0.1);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3);
  osc.connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.3);
}

/**
 * Burst Activate — Square chord burst (root + fifth + octave).
 * Duration: ~0.2s
 */
export function burst(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;
  const freqs = [220, 330, 440]; // root, fifth, octave

  for (const freq of freqs) {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
    osc.connect(gain).connect(dest);
    osc.start(t);
    osc.stop(t + 0.2);
  }
}

/**
 * DI Window — Filtered noise whoosh.
 * Duration: ~0.25s
 */
export function diWindow(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const noise = noiseSource(ctx);
  const bpf = ctx.createBiquadFilter();
  bpf.type = 'bandpass';
  bpf.frequency.setValueAtTime(500, t);
  bpf.frequency.exponentialRampToValueAtTime(3000, t + 0.15);
  bpf.Q.value = 2;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.01, t);
  gain.gain.linearRampToValueAtTime(0.35, t + 0.08);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.25);
  noise.connect(bpf).connect(gain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.25);
}

/**
 * Edgeguard Beam — Sawtooth buzz + noise.
 * Duration: ~0.4s
 */
export function edgeguardBeam(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  // Sawtooth buzz
  const saw = ctx.createOscillator();
  saw.type = 'sawtooth';
  saw.frequency.setValueAtTime(120, t);
  saw.frequency.linearRampToValueAtTime(180, t + 0.2);
  saw.frequency.linearRampToValueAtTime(90, t + 0.4);
  const sawGain = ctx.createGain();
  sawGain.gain.setValueAtTime(0.25, t);
  sawGain.gain.exponentialRampToValueAtTime(0.01, t + 0.4);
  saw.connect(sawGain).connect(dest);
  saw.start(t);
  saw.stop(t + 0.4);

  // Noise layer
  const noise = noiseSource(ctx);
  const hpf = ctx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = 4000;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.15, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, t + 0.35);
  noise.connect(hpf).connect(noiseGain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.4);
}

/**
 * Grapple Launch — Rising triangle chirp.
 * Duration: ~0.15s
 */
export function grappleLaunch(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(300, t);
  osc.frequency.exponentialRampToValueAtTime(1200, t + 0.12);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
  osc.connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.15);
}

/**
 * Wind Change — Low filtered noise sweep.
 * Duration: ~0.5s
 */
export function windChange(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const noise = noiseSource(ctx);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.setValueAtTime(200, t);
  lpf.frequency.exponentialRampToValueAtTime(800, t + 0.25);
  lpf.frequency.exponentialRampToValueAtTime(200, t + 0.5);
  lpf.Q.value = 3;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.01, t);
  gain.gain.linearRampToValueAtTime(0.25, t + 0.15);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.5);
  noise.connect(lpf).connect(gain).connect(dest);
  noise.start(t);
  noise.stop(t + 0.5);
}

/**
 * Announcement — Short bright chime.
 * Duration: ~0.1s
 */
export function announcement(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, t);
  osc.frequency.exponentialRampToValueAtTime(1600, t + 0.04);
  osc.frequency.exponentialRampToValueAtTime(1400, t + 0.1);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.25, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
  osc.connect(gain).connect(dest);
  osc.start(t);
  osc.stop(t + 0.1);
}
